import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import SiteFooter from '../../components/Footer/SiteFooter';
import SiteNavigation from '../../components/Navigation/SiteNavigation';
import './atlas.css';

const slides = [
  {
    title: 'Your digital life, brought into one clear view',
    desktop: '/atlas-website-image-package/atlas-website-image-package/desktop/atlas-slide-05-one-clear-view.png',
    mobile: '/atlas-website-image-package/atlas-website-image-package/mobile/atlas-mobile-slide-02-one-clear-view.png',
  },
  {
    title: 'The problem Atlas addresses',
    desktop: '/atlas-website-image-package/atlas-website-image-package/desktop/atlas-slide-01-before-after.png',
    mobile: '/atlas-missing-mobile-slides/mobile-02-problem-atlas-addresses.png',
  },
  {
    title: 'How Atlas works',
    desktop: '/atlas-website-image-package/atlas-website-image-package/desktop/atlas-slide-03-how-atlas-works.png',
    mobile: '/atlas-website-image-package/atlas-website-image-package/mobile/atlas-mobile-slide-01-how-atlas-works.png',
  },
  {
    title: 'Core principles',
    desktop: '/atlas-website-image-package/atlas-website-image-package/desktop/atlas-slide-02-core-principles.png',
    mobile: '/atlas-missing-mobile-slides/mobile-04-core-principles.png',
  },
];

const benefits = [
  ['Connect multiple locations', 'Bring folders from your computer, external drives, and other storage locations into one Atlas workspace.'],
  ['Find related files faster', 'Search and browse scattered photos, documents, projects, and other files from one centralized view.'],
  ['Leave originals in place', 'Atlas is designed to organize what you already have without forcing you to move, rename, or duplicate the original files.'],
];

export default function Atlas() {
  const [index, setIndex] = useState(0);
  const wheelLocked = useRef(false);
  const touchStart = useRef(null);
  const slide = slides[index];
  const isFirstSlide = index === 0;
  const isLastSlide = index === slides.length - 1;

  const move = (direction) => {
    setIndex((current) => Math.min(Math.max(current + direction, 0), slides.length - 1));
  };

  const handleWheel = (event) => {
    const movement = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.shiftKey
        ? event.deltaY
        : 0;

    if (Math.abs(movement) < 18 || wheelLocked.current) return;

    const direction = movement > 0 ? 1 : -1;
    const atBoundary = (direction < 0 && isFirstSlide) || (direction > 0 && isLastSlide);
    if (atBoundary) return;

    wheelLocked.current = true;
    move(direction);
    window.setTimeout(() => { wheelLocked.current = false; }, 500);
  };

  const handleTouchStart = (event) => {
    touchStart.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const end = event.changedTouches[0]?.clientX ?? touchStart.current;
    const movement = touchStart.current - end;
    touchStart.current = null;
    if (Math.abs(movement) > 45) move(movement > 0 ? 1 : -1);
  };

  return (
    <>
      <PageMeta
        title="How Atlas Organizes Files Across Folders and Drives | Atlas"
        description="Atlas creates one centralized workspace for files scattered across folders, computers, and external drives without moving or duplicating the originals."
        path="/atlas"
        image={slides[0].desktop}
      />
      <main className="atlas-story-page">
        <section
          className="atlas-story-hero"
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <SiteNavigation />

          <div className="atlas-artwork-stage" key={index} aria-live="polite">
            <h1 className="atlas-artwork-stage__sr-title">{slide.title}</h1>
            <picture className="atlas-artwork-frame">
              <source media="(max-width: 760px)" srcSet={slide.mobile} />
              <img src={slide.desktop} alt={slide.title} />
            </picture>
          </div>

          <div className="atlas-story-controls">
            <span>{index + 1} of {slides.length}</span>
            <button type="button" onClick={() => move(-1)} aria-label="Previous Atlas section" disabled={isFirstSlide}>←</button>
            <button type="button" className="is-primary" onClick={() => move(1)} aria-label="Next Atlas section" disabled={isLastSlide}>→</button>
          </div>
        </section>

        <section className="atlas-explainer">
          <div className="page-shell">
            <div className="atlas-explainer__intro">
              <p className="section-eyebrow">Organize files across folders and drives</p>
              <h2>One centralized workspace for the files you already have.</h2>
              <p>
                Atlas brings files scattered across your computer, external drives, and existing folders into one organized workspace. It helps you find, view, and work with related information without rebuilding your file system or moving the originals.
              </p>
            </div>

            <div className="atlas-benefit-grid">
              {benefits.map(([title, body]) => (
                <article className="atlas-benefit-card" key={title}>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>

            <div className="atlas-workflow">
              <div>
                <p className="section-eyebrow">Built around your existing workflow</p>
                <h2>Stop searching one folder and drive at a time.</h2>
              </div>
              <div className="atlas-workflow__body">
                <p>Choose the locations you already use. Atlas builds a clear view across those locations so scattered files can be found and organized together.</p>
                <p>Your folders and drives remain the source of truth. Atlas becomes the easier workspace above them.</p>
                <div className="atlas-explainer__actions">
                  <Link className="button button--primary" to="/pre-alpha">Apply for Pre-Alpha</Link>
                  <Link className="button button--secondary" to="/privacy">Read the privacy principles</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </main>
    </>
  );
}
